import React, { useState, useEffect } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	Typography,
	List,
	IconButton,
	Chip,
	CircularProgress,
	Alert,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	RadioGroup,
	FormControlLabel,
	Radio,
	FormControl,
	FormLabel,
	Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import LaunchIcon from '@mui/icons-material/Launch';
import { apiService } from '../services/api';

export interface WorkspaceItem {
	workspace_id: string;
	org_id: string;
	connection_id: string;
	owner_id: string | null;
	snapshot_id: string | null;
	container_app_id: string | null;
	container_app_url: string | null;
	file_share_name: string | null;
	infra_status: 'pending' | 'running' | 'completed' | 'failed';
	infra_started_at: string | null;
	infra_completed_at: string | null;
	infra_error_info: string | null;
	name: string;
	type: string;
	created_at: string;
	last_selected_at: string | null;
	connection: {
		connection_id: string;
		label: string;
		sf_org_id: string;
		sf_username: string;
		sf_instance_url: string;
		is_sandbox: boolean;
		created_at: string;
		last_used_at: string | null;
		last_selected_at: string | null;
	};
}

interface WorkspaceManagerProps {
	activeWorkspaceId: string | null;
	onWorkspaceSelect: (workspaceId: string) => void;
	onWorkspaceAdded?: () => void;
}

export const WorkspaceManager: React.FC<WorkspaceManagerProps> = ({
	activeWorkspaceId,
	onWorkspaceSelect,
	onWorkspaceAdded,
}) => {
	const [workspaces, setWorkspaces] = useState<WorkspaceItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [oauthDialogOpen, setOauthDialogOpen] = useState(false);
	const [oauthLoading, setOauthLoading] = useState(false);
	const [loginHostType, setLoginHostType] = useState<'production' | 'sandbox' | 'custom'>('production');
	const [customLoginHost, setCustomLoginHost] = useState('');
	const [packageInfo, setPackageInfo] = useState<{ name: string; namespace: string; installUrl: string; password: string } | null>(null);

	const loadWorkspaces = async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await apiService.getWorkspaces();
			if (response.success && response.data) {
				setWorkspaces(response.data.workspaces || []);
			} else {
				setError(response.error || 'Failed to load workspaces');
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to load workspaces');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadWorkspaces();
		loadAppConfig();
	}, []);

	const loadAppConfig = async () => {
		try {
			const response = await apiService.getAppConfig();
			if (response.success && response.data) {
				setPackageInfo(response.data.agenticServicesPackage);
			}
		} catch (err) {
			// Silently fail - package info is optional
			console.error('Failed to load app config:', err);
		}
	};

	const handleAddOrg = async () => {
		setOauthLoading(true);
		setError(null);
		try {
			// Determine login host based on selection
			let loginHost: string | undefined;
			if (loginHostType === 'production') {
				loginHost = 'login.salesforce.com';
			} else if (loginHostType === 'sandbox') {
				loginHost = 'test.salesforce.com';
			} else if (loginHostType === 'custom') {
				const trimmed = customLoginHost.trim();
				if (!trimmed) {
					setError('Please enter a custom login host');
					setOauthLoading(false);
					return;
				}
				// Remove protocol if present
				loginHost = trimmed.replace(/^https?:\/\//, '');
			}

			const response = await apiService.oauthStart(loginHost);
			if (response.success && response.data) {
				// Open OAuth URL in new window
				const authWindow = window.open(
					response.data.authorizeUrl,
					'salesforce-oauth',
					'width=600,height=700'
				);

				// Poll for completion (check if window is closed or listen for storage event)
				const checkInterval = setInterval(() => {
					if (authWindow?.closed) {
						clearInterval(checkInterval);
						setOauthDialogOpen(false);
						setOauthLoading(false);
						// Reload workspaces after OAuth completes
						setTimeout(() => {
							loadWorkspaces();
							if (onWorkspaceAdded) {
								onWorkspaceAdded();
							}
						}, 1000);
					}
				}, 500);

				// Also listen for storage event (set by OAuth callback page)
				const handleStorageChange = (e: StorageEvent) => {
					if (e.key === 'oauth_complete') {
						clearInterval(checkInterval);
						window.removeEventListener('storage', handleStorageChange);
						window.removeEventListener('message', handleMessage);
						setOauthDialogOpen(false);
						setOauthLoading(false);
						loadWorkspaces();
						if (onWorkspaceAdded) {
							onWorkspaceAdded();
						}
					}
				};
				window.addEventListener('storage', handleStorageChange);

				// Also listen for postMessage as backup (in case storage event doesn't fire)
				const handleMessage = (e: MessageEvent) => {
					if (e.data?.type === 'oauth_complete' && e.origin === window.location.origin) {
						clearInterval(checkInterval);
						window.removeEventListener('storage', handleStorageChange);
						window.removeEventListener('message', handleMessage);
						setOauthDialogOpen(false);
						setOauthLoading(false);
						loadWorkspaces();
						if (onWorkspaceAdded) {
							onWorkspaceAdded();
						}
					}
				};
				window.addEventListener('message', handleMessage);
			} else {
				setError(response.error || 'Failed to start OAuth flow');
				setOauthLoading(false);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to start OAuth flow');
			setOauthLoading(false);
		}
	};

	const formatDate = (dateString: string | null) => {
		if (!dateString) return 'Never';
		const date = new Date(dateString);
		return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
	};

	const getInfraChipProps = (status: WorkspaceItem['infra_status']) => {
		switch (status) {
			case 'completed':
				return { label: 'Ready', color: 'success' as const };
			case 'failed':
				return { label: 'Failed', color: 'error' as const };
			case 'running':
				return { label: 'Provisioning', color: 'warning' as const };
			default:
				return { label: 'Queued', color: 'warning' as const };
		}
	};

	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<Typography variant="h6">Workspaces</Typography>
				<Box>
					<IconButton onClick={loadWorkspaces} disabled={loading} size="small">
						<RefreshIcon />
					</IconButton>
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={() => setOauthDialogOpen(true)}
						size="small"
					>
						Add Org
					</Button>
				</Box>
			</Box>

			{error && (
				<Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			{loading ? (
				<Box display="flex" justifyContent="center" p={3}>
					<CircularProgress />
				</Box>
			) : workspaces.length === 0 ? (
				<Card>
					<CardContent>
						<Typography variant="body2" color="text.secondary" align="center">
							No workspaces found. Click "Add Org" to connect a Salesforce org.
						</Typography>
					</CardContent>
				</Card>
			) : (
				<List>
					{workspaces.map((workspace) => {
						const infraChip = getInfraChipProps(workspace.infra_status);
						return (
							<Card
								key={workspace.workspace_id}
								sx={{
									mb: 1,
									cursor: 'pointer',
									border:
										activeWorkspaceId === workspace.workspace_id
											? '2px solid primary.main'
											: '1px solid',
									'&:hover': {
										bgcolor: 'action.hover',
									},
								}}
								onClick={() => onWorkspaceSelect(workspace.workspace_id)}
							>
								<CardContent>
									<Box display="flex" justifyContent="space-between" alignItems="start">
										<Box flex={1}>
											<Typography variant="subtitle1">{workspace.connection.sf_instance_url}</Typography>
											<Typography variant="body2" color="text.secondary">
												{workspace.connection.sf_username}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												Org ID: {workspace.connection.sf_org_id}
											</Typography>
											<Box mt={1} display="flex" gap={1} flexWrap="wrap">
												<Chip
													label={workspace.connection.is_sandbox ? 'Sandbox' : 'Production'}
													size="small"
													color={workspace.connection.is_sandbox ? 'warning' : 'primary'}
												/>
												<Chip
													label={infraChip.label}
													size="small"
													color={infraChip.color}
												/>
											</Box>
											<Typography variant="caption" color="text.secondary" display="block" mt={1}>
												Last selected: {formatDate(workspace.last_selected_at)}
											</Typography>
										</Box>
										{activeWorkspaceId === workspace.workspace_id && (
											<Chip label="Active" color="primary" size="small" />
										)}
									</Box>
								</CardContent>
							</Card>
						);
					})}
				</List>
			)}

			<Dialog open={oauthDialogOpen} onClose={() => !oauthLoading && setOauthDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Add Salesforce Org</DialogTitle>
				<DialogContent>
					{oauthLoading ? (
						<Box display="flex" flexDirection="column" alignItems="center" p={2}>
							<CircularProgress sx={{ mb: 2 }} />
							<Typography variant="body2" color="text.secondary">
								Opening Salesforce authorization page...
							</Typography>
							<Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
								Please complete the authorization in the popup window.
							</Typography>
						</Box>
					) : (
						<Box sx={{ pt: 2 }}>
							{packageInfo && packageInfo.installUrl && (
								<Alert severity="info" sx={{ mb: 3 }}>
									<Typography variant="body2" sx={{ mb: 1 }}>
										<strong>Required Managed Package</strong>
									</Typography>
									<Typography variant="body2" sx={{ mb: 1 }}>
										In order to connect a Salesforce org, you must have the <strong>{packageInfo.name}</strong> managed package (namespace: <strong>{packageInfo.namespace}</strong>) installed in your org.
									</Typography>
									{packageInfo.password && (
										<Typography variant="body2" sx={{ mb: 1 }}>
											<strong>Installation Password:</strong> <code>{packageInfo.password}</code>
										</Typography>
									)}
									<Link
										href={packageInfo.installUrl}
										target="_blank"
										rel="noopener noreferrer"
										underline="hover"
										sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
									>
										Install Package
										<LaunchIcon fontSize="small" />
									</Link>
								</Alert>
							)}
							<FormControl component="fieldset" fullWidth>
								<FormLabel component="legend">Login Host</FormLabel>
								<RadioGroup
									value={loginHostType}
									onChange={(e) => setLoginHostType(e.target.value as 'production' | 'sandbox' | 'custom')}
									sx={{ mt: 1 }}
								>
									<FormControlLabel
										value="production"
										control={<Radio />}
										label="Production (login.salesforce.com)"
									/>
									<FormControlLabel
										value="sandbox"
										control={<Radio />}
										label="Sandbox (test.salesforce.com)"
									/>
									<FormControlLabel
										value="custom"
										control={<Radio />}
										label="Custom MyDomain"
									/>
								</RadioGroup>
							</FormControl>
							{loginHostType === 'custom' && (
								<TextField
									fullWidth
									label="Custom Login Host"
									placeholder="e.g., mycompany.salesforce.com"
									value={customLoginHost}
									onChange={(e) => setCustomLoginHost(e.target.value)}
									margin="normal"
									helperText="Enter your Salesforce MyDomain (without https://)"
								/>
							)}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleAddOrg} variant="contained" disabled={oauthLoading}>
						Start Authorization
					</Button>
					<Button
						onClick={() => {
							setOauthDialogOpen(false);
							setLoginHostType('production');
							setCustomLoginHost('');
						}}
						disabled={oauthLoading}
					>
						Cancel
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
